import React, { useCallback, useEffect, useState } from 'react';
import {
  ArrowRight, Ban, CheckCircle2, ChevronDown, ChevronLeft, ChevronRight,
  Gamepad2, Gift, Headphones, Loader2, Play,
  Target, Ticket, TicketPercent, X,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import client, { resolveApiAssetUrl } from '../lib/api';
import PromoTelegramGate from '../components/PromoTelegramGate';
import { usePromoTelegram } from '../hooks/usePromoTelegram';
import GameModal from '../components/GameModal';
import { useGamePage } from '../hooks/useGamePage';
import ProgressFill from '../components/ProgressFill';
import {
  HOOKS, accountHook, bonusHook, bonusIdHook, hook,
} from '../lib/domHooks';
import RichHtmlFrame from '../components/RichHtmlFrame';
import BonusCardHtml from '../components/BonusCardHtml';
import BonusHtmlThumb, { bonusHasHtmlArt } from '../components/BonusHtmlThumb';
import { fetchBonusDescriptionHtml } from '../lib/bonusDescriptionHtml';
import { liveChatState, openLiveChat } from '../components/liveChatConfig';
import { formatBonusEligible, parseBonusEligible } from '../constants/bonusEligibility';
import { useActiveTheme } from '../lib/activeTheme';
import { VerticalCard as LossBonusDetail, useLossBonus } from './account/LossBonus';
import {
  LossBonusCard, LossBonusImageTile, lossBlockMatchesFilter,
} from './account/LossBonusTile';
import { bonusHighlight } from '../lib/bonusHighlight';
import {
  bonusClaimAction, bonusPrimaryMetric, bonusRewardHeadline,
  freespinRewardText, isCashTierBonus, isFreespinOnlyBonus,
} from '../lib/bonusReward';
import {
  REWARD_CASH, bonusMaxBonus, bonusMinDeposit, bonusWindowText, isAutoBonus,
  tierLines,
} from '../lib/bonusRules';

const FREEBET_STATUS = {
  active: { label: 'Kullanıma Hazır', className: 'text-emerald-300' },
  used: { label: 'Kullanıldı', className: 'text-sky-300' },
  expired: { label: 'Süresi Doldu', className: 'text-slate-400' },
};

const shortDate = (value) => {
  const date = new Date(value || 0);
  return Number.isFinite(date.getTime()) && date.getTime() > 0
    ? date.toLocaleDateString('tr-TR')
    : '';
};

/** Kart başlığındaki "Aktif / Geçmiş" seçicisi.
 *
 * Kullanılmış haklar aktif listeyi doldurmasın diye ayrılır; geçmiş yoksa
 * seçici hiç çizilmez (boş bir sekme göstermenin anlamı yok).
 */
const HistoryTabs = ({ view, onChange, activeCount, pastCount }) => (
  <div className="ml-auto flex shrink-0 gap-1 rounded-lg border border-white/10 bg-black/25 p-1" {...accountHook('tabs')}>
    {[
      { key: 'active', label: 'Aktif', count: activeCount },
      { key: 'past', label: 'Geçmiş', count: pastCount },
    ].map((tab) => (
      <button
        key={tab.key}
        type="button"
        onClick={() => onChange(tab.key)}
        aria-pressed={view === tab.key}
        {...accountHook('tab')}
        className={`rounded-md px-3 py-1.5 text-xs font-bold transition ${
          view === tab.key
            ? 'bg-white/10 text-white'
            : 'text-gray-400 hover:text-gray-200'
        }`}
      >
        {tab.label} ({tab.count})
      </button>
    ))}
  </div>
);

/** Kartın açık/kapalı tercihi — oyuncunun kendi tarayıcısında durur.
 *
 * Sayfa üç kart taşıyor ve hepsi birden açıkken listeler ekranı aşıyor;
 * kapattığı kartın kapalı kalmasını beklemek oyuncunun hakkıdır. Depolama
 * okunamazsa (gizli sekme, engellenmiş site verisi) kart açık açılır.
 */
const useSectionOpen = (key) => {
  const storageKey = `promo:section:${key}`;
  const [open, setOpen] = useState(() => {
    try {
      return window.localStorage.getItem(storageKey) !== 'closed';
    } catch {
      return true;
    }
  });
  const toggle = useCallback(() => {
    setOpen((current) => {
      const next = !current;
      try {
        window.localStorage.setItem(storageKey, next ? 'open' : 'closed');
      } catch {
        /* tercih saklanamıyorsa kart yine de açılıp kapanmalı */
      }
      return next;
    });
  }, [storageKey]);
  return [open, toggle];
};

/** Kartların açılır kapanır başlığı.
 *
 * Başlığın tamamı tıklanabilir olmalı ama içinde HistoryTabs'ın kendi
 * düğmeleri duruyor; iç içe <button> geçersiz olduğu için kapsayıcı
 * role="button" ile kurulur ve sekmelerin tıklaması yukarı taşınmaz —
 * aksi hâlde "Geçmiş" sekmesine basmak kartı da kapatırdı.
 */
const CollapsibleHeader = ({
  bodyId, titleId, icon, title, description, tone, open, onToggle, tabs = null,
}) => (
  <div
    role="button"
    tabIndex={0}
    aria-expanded={open}
    aria-controls={bodyId}
    onClick={onToggle}
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onToggle();
      }
    }}
    className={`cursor-pointer select-none border-b border-white/5 px-4 py-3 md:px-5 ${tone}`}
    {...accountHook('header')}
  >
    <div className="flex flex-wrap items-center gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          {icon}
          <h2 id={titleId} className="font-black text-white" {...accountHook('title')}>{title}</h2>
        </div>
        <p className="mt-1 text-xs text-gray-400" {...accountHook('subtitle')}>{description}</p>
      </div>
      {tabs && (
        <div
          role="presentation"
          className="ml-auto flex shrink-0"
          onClick={(event) => event.stopPropagation()}
        >
          {tabs}
        </div>
      )}
      <ChevronDown
        size={18}
        aria-hidden="true"
        className={`shrink-0 text-gray-400 transition-transform ${
          tabs ? '' : 'ml-auto'
        } ${open ? 'rotate-180' : ''}`}
      />
    </div>
  </div>
);

export const FreebetRights = ({ items }) => {
  const [open, toggleOpen] = useSectionOpen('freebets');
  if (!items.length) return null;
  return (
    <section className="pusu-card mb-6 overflow-hidden" aria-labelledby="my-freebets-title" {...accountHook('freebet-rights')}>
      <CollapsibleHeader
        bodyId="my-freebets-body"
        titleId="my-freebets-title"
        icon={<Ticket size={18} className="text-emerald-300" />}
        title="Freebet Haklarım"
        description="Çapa ve promosyonlardan kazandığınız ücretsiz bahis hakları"
        tone="bg-emerald-500/10"
        open={open}
        onToggle={toggleOpen}
      />
      {open && (
      <div id="my-freebets-body" className="grid gap-3 p-4 md:p-5" {...accountHook('list')}>
        {items.map((item) => {
          const status = FREEBET_STATUS[item.status] || FREEBET_STATUS.active;
          return (
            <div key={item.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-white/10 bg-black/20 p-4" {...accountHook('row')}>
              <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-emerald-500/10">
                <Ticket size={18} className="text-emerald-300" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-bold text-white">
                  {item.title || 'Freebet Hakkı'}
                </div>
                <div className="mt-1 text-xs text-gray-400">
                  En az {Number(item.min_odd || 1).toFixed(2)} toplam oran
                  {item.expires_at
                    ? ` • Son kullanım ${new Date(item.expires_at).toLocaleDateString('tr-TR')}`
                    : ''}
                </div>
              </div>
              <div className="text-right">
                <div className="text-xl font-black text-emerald-300">
                  {Number(item.amount || 0).toFixed(2)} ₺
                </div>
                <div className={`text-[11px] font-bold ${status.className}`} {...accountHook('badge')}>
                  {status.label}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      )}
    </section>
  );
};

const FREESPIN_STATUS = {
  completed: { label: 'Tanımlandı', className: 'text-emerald-300' },
  pending: { label: 'Tanımlanıyor', className: 'text-amber-300' },
  awaiting_provider: { label: 'Tanımlanıyor', className: 'text-amber-300' },
  cancelled: { label: 'İptal Edildi', className: 'text-rose-300' },
};

/** Bonus kampanyasının freespin ayağını "Freespin Haklarım" satırına çevirir.
 *
 * Kampanyadan verilen freespin sağlayıcıda tanımlıdır ama oyuncunun ekranında
 * hiçbir izi yoktu: para ayağı kapalı kampanyada bonus kartı bile "0,00 ₺"
 * gösteriyor, oyuncu "freespin gelmedi" diyordu. Çapa ödülleriyle aynı kartta
 * çizilebilmesi için alan adları o biçime uydurulur.
 */
export const bonusFreespinRights = (claims) => (
  (Array.isArray(claims) ? claims : [])
    .filter((claimItem) => (
      claimItem.freespin_status && Number(claimItem.freespin_count || 0) > 0
    ))
    .map((claimItem) => ({
      id: `bonus-${claimItem.id}`,
      // İptal ucu kampanya kaydının kendi kimliğini ister; kart kimliği
      // çapa haklarıyla çakışmasın diye öneklidir.
      claim_id: claimItem.id,
      cancellable: !!claimItem.freespin_cancellable,
      created_at: claimItem.created_at,
      reward_type: 'freespin',
      source: 'bonus',
      title: claimItem.bonus_title || '',
      spin_count: Number(claimItem.freespin_count || 0),
      // Sağlayıcı kalan spini bildirmiyorsa (Gold Palace) sunucu hareket
      // tablosundan sayar; gelmediğinde hak tam sayılır. Biten hak böylece
      // geçmişe düşer ve iptal düğmesi kalkar (splitFreespinRights).
      ...(claimItem.freespin_remaining !== undefined
        && claimItem.freespin_remaining !== null
        ? { remaining_spins: Number(claimItem.freespin_remaining) }
        : {}),
      status: claimItem.freespin_status,
      game: claimItem.freespin_game || (
        claimItem.freespin_game_name
          ? { name: claimItem.freespin_game_name }
          : null
      ),
    }))
);

/** Talep yanıtını oyuncuya okunur bir özete çevirir.
 *
 * Ödülün iki ayağı bağımsız olduğu için özet de öyle kurulur: yalnız freespin
 * veren kampanyada tutar sıfırdır ve "+0,00 ₺" mesajı oyuncuya "bonus
 * verilmedi" gibi görünüyordu.
 */
export const claimSummary = (data) => {
  const amount = Number(data?.amount || 0);
  const wager = Number(data?.wager_required || 0);
  const spins = Number(data?.freespin_count || 0);
  const parts = [];
  if (amount > 0) {
    parts.push(
      `+${amount.toFixed(2)} ₺`
      + (wager > 0 ? ` • Çevrim: ${wager.toFixed(0)} ₺` : ''),
    );
  }
  if (spins > 0) {
    const game = data?.freespin_game ? ` • ${data.freespin_game}` : '';
    parts.push(
      `${spins} freespin${game}`
      + (data?.freespin_status === 'awaiting_provider'
        ? ' (kısa süre içinde tanımlanacak)'
        : ''),
    );
  }
  return parts.join(' • ') || 'Bonusunuz hesabınıza tanımlandı';
};

/** Çapa ve bonus kaynaklı freespin haklarını tek listede, yeniden eskiye. */
export const mergeFreespinRights = (anchorItems, claims) => [
  ...(Array.isArray(anchorItems) ? anchorItems : []).filter(
    (item) => item.reward_type === 'freespin',
  ),
  ...bonusFreespinRights(claims),
].sort(
  (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0),
);

const freespinWaitSeconds = (availableAt, now) => {
  const readyAt = new Date(availableAt || 0).getTime();
  if (!Number.isFinite(readyAt) || readyAt <= now) return 0;
  return Math.ceil((readyAt - now) / 1000);
};

export const FREESPIN_PAGE_SIZE = 3;

export const pageItems = (items, page, pageSize = FREESPIN_PAGE_SIZE) => {
  const safeItems = Array.isArray(items) ? items : [];
  const size = Math.max(1, Number(pageSize) || FREESPIN_PAGE_SIZE);
  const pageCount = Math.max(1, Math.ceil(safeItems.length / size));
  const safePage = Math.min(pageCount - 1, Math.max(0, Number(page) || 0));
  return {
    items: safeItems.slice(safePage * size, (safePage + 1) * size),
    page: safePage,
    pageCount,
  };
};

export const freespinRemaining = (item) => {
  const total = Number(item.spin_count || item.value || 0);
  const reported = Number(item.remaining_spins);
  return Number.isFinite(reported)
    ? Math.max(0, reported)
    : Math.max(0, total);
};

/** Tükenen ve iptal edilen haklar geçmişe düşer, kalanlar aktif listede. */
export const splitFreespinRights = (items) => {
  const active = [];
  const past = [];
  (Array.isArray(items) ? items : []).forEach((item) => {
    const exhausted = (
      item.status === 'completed' && freespinRemaining(item) <= 0
    );
    (exhausted || item.status === 'cancelled' ? past : active).push(item);
  });
  return { active, past };
};

export const FreespinRights = ({
  items, onPlay = () => {}, onCancel = null, cancellingId = null,
  initialView = 'active',
}) => {
  const [clock, setClock] = useState(() => Date.now());
  const [page, setPage] = useState(0);
  const [view, setView] = useState(initialView);
  const [open, toggleOpen] = useSectionOpen('freespins');
  const { active, past } = splitFreespinRights(items);
  const visible = view === 'past' ? past : active;
  const pageData = pageItems(visible, page);
  useEffect(() => {
    const waiting = items.some(
      (item) => freespinWaitSeconds(item.available_at, Date.now()) > 0,
    );
    if (!waiting) return undefined;
    const timer = window.setInterval(() => setClock(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [items]);
  useEffect(() => {
    if (page !== pageData.page) setPage(pageData.page);
  }, [page, pageData.page]);
  // Son geçmiş kayıt listeden düşerse boş bir sekmede kalınmasın.
  useEffect(() => {
    if (view === 'past' && !past.length) setView('active');
  }, [past.length, view]);

  if (!active.length && !past.length) return null;
  return (
    <section className="pusu-card mb-6 overflow-hidden" aria-labelledby="my-freespins-title" {...accountHook('freespin-rights')}>
      <CollapsibleHeader
        bodyId="my-freespins-body"
        titleId="my-freespins-title"
        icon={<Gamepad2 size={18} className="text-sky-300" />}
        title="Freespin Haklarım"
        description={view === 'past'
          ? 'Tükenen ve kapanan freespin haklarınız'
          : 'Freespinlerin yalnızca aşağıda belirtilen oyunda kullanılabilir'}
        tone="bg-sky-500/10"
        open={open}
        onToggle={toggleOpen}
        tabs={past.length > 0 && (
          <HistoryTabs
            view={view}
            onChange={(next) => { setView(next); setPage(0); }}
            activeCount={active.length}
            pastCount={past.length}
          />
        )}
      />
      {open && (
      <>
      <div id="my-freespins-body" className="grid gap-3 p-4 md:p-5" {...accountHook('list')}>
        {!visible.length && (
          <p className="py-6 text-center text-sm text-gray-500" {...accountHook('empty')}>
            Kullanabileceğiniz freespin hakkınız yok.
          </p>
        )}
        {pageData.items.map((item) => {
          const totalSpins = Number(item.spin_count || item.value || 0);
          const remainingSpins = freespinRemaining(item);
          const waitSeconds = freespinWaitSeconds(item.available_at, clock);
          const activating = item.status === 'completed' && waitSeconds > 0;
          const exhausted = item.status === 'completed' && remainingSpins <= 0;
          const status = exhausted
            ? { label: 'Tükendi', className: 'text-slate-400' }
            : activating
            ? {
              label: `Etkinleşiyor (${waitSeconds} sn)`,
              className: 'text-amber-300',
            }
            : FREESPIN_STATUS[item.status]
              || FREESPIN_STATUS.awaiting_provider;
          const gameReady = (
            item.status === 'completed' && !activating
            && remainingSpins > 0
            && item.game?.id && item.game?.active
          );
          return (
            <div key={item.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-white/10 bg-black/20 p-4" {...accountHook('row')}>
              <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-sky-500/10">
                <Gamepad2 size={18} className="text-sky-300" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-bold text-white">
                  {totalSpins} Freespin
                  {item.title ? (
                    <span className="ml-2 text-xs font-semibold text-gray-400">
                      {item.title}
                    </span>
                  ) : null}
                </div>
                <div className="mt-1 text-xs text-gray-400">
                  {item.game?.name
                    ? `Sadece ${item.game.name} oyununda geçerlidir`
                    : item.status === 'awaiting_provider' || item.status === 'pending'
                      ? 'Oyun ve freespin tanımlaması hazırlanıyor'
                      : 'Oyun bilgisi bulunamadı; destek ekibiyle iletişime geçin'}
                </div>
                <div className="mt-2 text-sm font-black text-sky-300">
                  Kalan: {remainingSpins} FS
                </div>
              </div>
              <div className="ml-auto flex items-center gap-3" {...accountHook('actions')}>
                <div className="text-right">
                  <div className={`text-[11px] font-bold ${status.className}`} {...accountHook('badge')}>
                    {status.label}
                  </div>
                  {view === 'past' && shortDate(item.created_at) && (
                    <div className="mt-0.5 text-[11px] text-gray-500">
                      {shortDate(item.created_at)}
                    </div>
                  )}
                </div>
                {gameReady && (
                  <button
                    type="button"
                    onClick={() => onPlay(item.game)}
                    className="flex items-center gap-1.5 rounded-lg bg-sky-500 px-3 py-2 text-xs font-black text-sky-950 hover:bg-sky-400"
                  >
                    <Play size={13} fill="currentColor" /> Oyunu Aç
                  </button>
                )}
                {/* İptal edilebilirlik sunucudan gelir: sağlayıcısı hakkı geri
                    alamıyorsa düğme hiç çizilmez, yoksa oyuncu iptal ettiğini
                    sanıp spinleri oyunda görmeye devam ederdi. */}
                {onCancel && item.cancellable && !exhausted && (
                  <button
                    type="button"
                    disabled={cancellingId === item.id}
                    onClick={() => onCancel(item)}
                    className="flex items-center gap-1.5 rounded-lg border border-rose-400/30 px-3 py-2 text-xs font-black text-rose-300 hover:bg-rose-500/10 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {cancellingId === item.id
                      ? <Loader2 size={13} className="animate-spin" />
                      : <Ban size={13} />}
                    İptal Et
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {pageData.pageCount > 1 && (
        <div className="flex items-center justify-between border-t border-white/5 px-4 py-3 md:px-5" {...accountHook('pagination')}>
          <button
            type="button"
            disabled={pageData.page === 0}
            onClick={() => setPage((current) => Math.max(0, current - 1))}
            className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-2 text-xs font-bold text-gray-200 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ChevronLeft size={14} /> Önceki
          </button>
          <span className="text-xs font-bold text-gray-400">
            Sayfa {pageData.page + 1} / {pageData.pageCount}
          </span>
          <button
            type="button"
            disabled={pageData.page + 1 >= pageData.pageCount}
            onClick={() => setPage((current) => Math.min(
              pageData.pageCount - 1, current + 1,
            ))}
            className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-2 text-xs font-bold text-gray-200 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Sonraki <ChevronRight size={14} />
          </button>
        </div>
      )}
      </>
      )}
    </section>
  );
};

const BONUS_CLAIM_STATUS = {
  completed: { label: 'Çevrim Tamamlandı', className: 'text-emerald-300' },
  cancelled: { label: 'İptal Edildi', className: 'text-rose-300' },
  expired: { label: 'Süresi Doldu', className: 'text-slate-400' },
  // Bonus bakiyesi tükendi: çevrim yükümlülüğü de kapandı.
  lost: { label: 'Bonus Bakiyesi Bitti', className: 'text-slate-400' },
};

export const claimRemainingWager = (claimItem) => Math.max(
  0,
  Number(
    claimItem.wager_remaining
    ?? Number(claimItem.wager_required || 0)
      - Number(claimItem.wager_completed || 0),
  ),
);

/** Çevrimi süren bonus aktiftir; kapanan ya da çevrimi biten geçmişe düşer. */
export const splitBonusClaims = (items) => {
  const active = [];
  const past = [];
  (Array.isArray(items) ? items : []).forEach((claimItem) => {
    const running = (
      claimItem.status === 'active' && claimRemainingWager(claimItem) > 0
    );
    (running ? active : past).push(claimItem);
  });
  return { active, past };
};

export const BonusClaims = ({
  items, onCancel = null, cancellingId = null, initialView = 'active',
}) => {
  const [view, setView] = useState(initialView);
  const [page, setPage] = useState(0);
  const [open, toggleOpen] = useSectionOpen('bonus');
  const { active, past } = splitBonusClaims(items);
  const visible = view === 'past' ? past : active;
  const pageData = pageItems(visible, page);
  useEffect(() => {
    if (page !== pageData.page) setPage(pageData.page);
  }, [page, pageData.page]);
  useEffect(() => {
    if (view === 'past' && !past.length) setView('active');
  }, [past.length, view]);

  if (!active.length && !past.length) return null;

  const progressPercent = (claimItem) => {
    if (claimItem.progress_percent != null) {
      return Math.min(100, Math.max(0, Number(claimItem.progress_percent)));
    }
    const required = Number(claimItem.wager_required || 0);
    return required > 0
      ? Math.min(100, Number(claimItem.wager_completed || 0) / required * 100)
      : 100;
  };

  return (
    <section className="pusu-card mb-6 overflow-hidden" aria-labelledby="my-bonus-title" {...accountHook('bonus-claims')}>
      <CollapsibleHeader
        bodyId="my-bonus-body"
        titleId="my-bonus-title"
        icon={<Target size={18} className="text-[var(--pusu-gold)]" />}
        title="Benim Bonusum"
        description={view === 'past'
          ? 'Tamamlanan ve kapanan bonuslarınız'
          : 'Aktif bonusunuz ve tamamlamanız gereken kalan çevrim'}
        tone="bg-[var(--pusu-gold)]/10"
        open={open}
        onToggle={toggleOpen}
        tabs={past.length > 0 && (
          <HistoryTabs
            view={view}
            onChange={(next) => { setView(next); setPage(0); }}
            activeCount={active.length}
            pastCount={past.length}
          />
        )}
      />
      {open && (
      <>
      <div id="my-bonus-body" className="grid gap-4 p-4 md:p-5" {...accountHook('list')}>
        {!visible.length && (
          <p className="py-6 text-center text-sm text-gray-500" {...accountHook('empty')}>
            Şu anda çevrimi süren bir bonusunuz yok.
          </p>
        )}
        {pageData.items.map((claimItem) => {
          if (view === 'past') {
            const status = BONUS_CLAIM_STATUS[claimItem.status]
              || BONUS_CLAIM_STATUS.completed;
            return (
              <div key={claimItem.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-white/10 bg-black/20 p-4" {...accountHook('row')}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-bold text-white">{claimItem.bonus_title}</div>
                  <div className="mt-1 text-xs text-gray-500">
                    {Number(claimItem.amount || 0) > 0 ? (
                      <>
                        Bonus tutarı: <span className="font-semibold text-green-400">{Number(claimItem.amount).toFixed(2)} ₺</span>
                        {Number(claimItem.wager_required || 0) > 0
                          && ` • Çevrim: ${Number(claimItem.wager_required).toFixed(2)} ₺`}
                      </>
                    ) : (
                      // Yalnız freespin veren kampanyada tutar sıfırdır;
                      // "0,00 ₺" yazmak ödülü verilmemiş gibi gösteriyordu.
                      <>
                        Ödül:
                        {' '}
                        <span className="font-semibold text-sky-300">
                          {Number(claimItem.freespin_count || 0) > 0
                            ? `${Number(claimItem.freespin_count)} freespin`
                            : '—'}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <div className={`text-[11px] font-bold ${status.className}`} {...accountHook('badge')}>
                    {status.label}
                  </div>
                  {shortDate(claimItem.created_at) && (
                    <div className="mt-0.5 text-[11px] text-gray-500">
                      {shortDate(claimItem.created_at)}
                    </div>
                  )}
                </div>
              </div>
            );
          }
          const remaining = claimRemainingWager(claimItem);
          const progress = progressPercent(claimItem);
          return (
            <div key={claimItem.id} className="rounded-xl border border-white/10 bg-black/20 p-4" {...accountHook('row')}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-sm font-bold text-white">{claimItem.bonus_title}</div>
                  <div className="mt-1 text-xs text-gray-500">
                    Bonus tutarı: <span className="font-semibold text-green-400">{Number(claimItem.amount || 0).toFixed(2)} ₺</span>
                  </div>
                </div>
                <div className="shrink-0 rounded-lg bg-yellow-400/10 px-3 py-2 text-right" {...accountHook('wager')}>
                  <div className="text-[10px] font-bold uppercase tracking-wide text-yellow-500">Kalan Çevrim</div>
                  <div className="mt-0.5 text-lg font-black text-yellow-400">{remaining.toFixed(2)} ₺</div>
                </div>
              </div>
              <div className="mt-4 h-2 overflow-hidden rounded-full bg-white/5" {...accountHook('progress')}>
                <ProgressFill
                  percent={progress}
                  className="h-full rounded-full bg-gradient-to-r from-[#1d9c61] to-[#36d79b] transition-all"
                />
              </div>
              <div className="mt-2 flex justify-between text-[11px] text-gray-500">
                <span>{Number(claimItem.wager_completed || 0).toFixed(2)} ₺ tamamlandı</span>
                <span>Toplam {Number(claimItem.wager_required || 0).toFixed(2)} ₺</span>
              </div>
              {/* Çevrimi tamamlamak istemeyen oyuncunun çıkışı: aktif çevrim
                  para çekmeyi kapattığı için bonustan vazgeçebilmelidir. */}
              {onCancel && claimItem.cancellable !== false && (
                <div className="mt-4 flex items-center justify-between gap-3 border-t border-white/5 pt-3" {...accountHook('actions')}>
                  <p className="text-[11px] text-gray-500">
                    Çevrimi tamamlamak istemiyorsanız bonustan vazgeçebilirsiniz;
                    bonus bakiyeniz silinir, kendi paranıza dokunulmaz.
                  </p>
                  <button
                    type="button"
                    disabled={cancellingId === claimItem.id}
                    onClick={() => onCancel(claimItem)}
                    className="flex shrink-0 items-center gap-1.5 rounded-lg border border-rose-400/30 px-3 py-2 text-xs font-black text-rose-300 hover:bg-rose-500/10 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {cancellingId === claimItem.id
                      ? <Loader2 size={13} className="animate-spin" />
                      : <Ban size={13} />}
                    Bonusu İptal Et
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {pageData.pageCount > 1 && (
        <div className="flex items-center justify-between border-t border-white/5 px-4 py-3 md:px-5" {...accountHook('pagination')}>
          <button
            type="button"
            disabled={pageData.page === 0}
            onClick={() => setPage((current) => Math.max(0, current - 1))}
            className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-2 text-xs font-bold text-gray-200 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ChevronLeft size={14} /> Önceki
          </button>
          <span className="text-xs font-bold text-gray-400">
            Sayfa {pageData.page + 1} / {pageData.pageCount}
          </span>
          <button
            type="button"
            disabled={pageData.page + 1 >= pageData.pageCount}
            onClick={() => setPage((current) => Math.min(
              pageData.pageCount - 1, current + 1,
            ))}
            className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-2 text-xs font-bold text-gray-200 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Sonraki <ChevronRight size={14} />
          </button>
        </div>
      )}
      </>
      )}
    </section>
  );
};

// Kartın açıklama satırı. Panelde "Kartta da tasarımıyla göster" açıksa HTML
// şablon kırpılmış bir çerçevede çizilir; şablon gelene kadar (ya da
// alınamazsa) düz özet durur, kart hiçbir anda boş kalmaz.
const BonusCardDescription = ({ bonus, onOpen }) => {
  // Görseli olmayan kampanyada şablon zaten görsel alanında çizilir
  // (BonusHtmlThumb); aynı tasarımı altında ikinci kez çizmek kartı uzatırdı.
  const artInHeader = bonusHasHtmlArt(bonus);
  const wantsHtml = !!(bonus.card_html_enabled && bonus.description_html) && !artInHeader;
  const [html, setHtml] = useState('');
  useEffect(() => {
    if (!wantsHtml) return undefined;
    let cancelled = false;
    fetchBonusDescriptionHtml(bonus.id)
      .then((value) => { if (!cancelled) setHtml(value); })
      .catch(() => { if (!cancelled) setHtml(''); });
    return () => { cancelled = true; };
  }, [wantsHtml, bonus.id]);
  if (artInHeader) return null;
  if (wantsHtml && html) {
    return (
      <div {...bonusHook('description')}>
        <BonusCardHtml html={html} title={bonus.title} onOpen={onOpen} className="mt-2" />
      </div>
    );
  }
  return (
    <p className="text-sm text-gray-400 mt-2 line-clamp-2" {...bonusHook('description')}>
      {bonus.description}
    </p>
  );
};

/** Oyuncunun kendi ödülleri: çevrimi süren bonus, freespin ve freebet hakları.
 *
 * Hesap bölümündeki Bonuslarım sayfasında çizilir (`/hesap/bonuslarim`,
 * pages/account/MyBonuses.jsx). Bir süre kampanya listesinin altında da
 * duruyordu ve açık kalan kartlar kampanyaları ekranın altına itiyordu —
 * oyuncu aktif bonusunu görmek için kampanyaları geçmek zorundaydı.
 * İstekleri kendisi atar; `version` değiştiğinde listeyi tazeler.
 */
export const MyRewards = ({ version = 0, emptyState = null }) => {
  const [loaded, setLoaded] = useState(false);
  const [myClaims, setMyClaims] = useState([]);
  const [myFreebets, setMyFreebets] = useState([]);
  const [myFreespins, setMyFreespins] = useState([]);
  const [activeFreespinGame, setActiveFreespinGame] = useState(null);
  // Oyun sayfası kullanan şemada (Betrun) ödül oyunu pencerede değil kendi
  // sayfasında açılır (bkz. hooks/useGamePage.js).
  const openGamePage = useGamePage();
  const playRewardGame = useCallback(
    (game) => { if (!openGamePage(game)) setActiveFreespinGame(game); },
    [openGamePage],
  );
  const [cancellingClaimId, setCancellingClaimId] = useState(null);
  const [cancellingFreespinId, setCancellingFreespinId] = useState(null);
  const { toast } = useToast();
  const { user, setWalletBalances } = useAuth();
  const userId = user?.id;

  useEffect(() => {
    if (!userId) {
      setMyClaims([]);
      setMyFreebets([]);
      setMyFreespins([]);
      return undefined;
    }
    let cancelled = false;
    Promise.all([
      client.get('/bonuses/claims/me').catch(() => ({ data: { items: [] } })),
      client.get('/finance/freebets').catch(() => ({ data: { items: [] } })),
      client.get('/anchor/history?limit=50').catch(() => ({ data: { items: [] } })),
    ]).then(([claimsResponse, freebetsResponse, anchorResponse]) => {
      if (cancelled) return;
      const claimItems = claimsResponse?.data?.items || [];
      setMyClaims(claimItems);
      setMyFreebets(freebetsResponse?.data?.items || []);
      // Çapa ödülleri ile bonus kampanyalarının freespin ayağı aynı kartta
      // durur: oyuncu için ikisi de "elimdeki freespin"dir, kaynağı değil.
      setMyFreespins(mergeFreespinRights(
        anchorResponse?.data?.items || [], claimItems,
      ));
      setLoaded(true);
    });
    return () => { cancelled = true; };
  }, [userId, version]);

  /** Bonus ve freespin kartlarını sunucudaki güncel hâliyle tazeler.
   *
   * İstek başarısız olursa listeler **olduğu gibi bırakılır**: boş listeye
   * düşmek, ödülü duran oyuncuya "hakkım kayboldu" diye görünürdü.
   */
  const refreshRewards = useCallback(async () => {
    try {
      const [claimsResponse, anchorResponse] = await Promise.all([
        client.get('/bonuses/claims/me'),
        client.get('/anchor/history?limit=50'),
      ]);
      const claimItems = claimsResponse?.data?.items || [];
      setMyClaims(claimItems);
      setMyFreespins(mergeFreespinRights(
        anchorResponse?.data?.items || [], claimItems,
      ));
    } catch {
      // Yalnızca özet yenilemesinin hatası, iptalin kendisini başarısız
      // göstermemelidir.
    }
  }, []);

  /** Çevrimi süren bonustan vazgeçme.
   *
   * Aktif çevrim para çekmeyi kapatır; onay metni bu yüzden ne kazanıldığını
   * (çevrim kalkar) ve ne kaybedildiğini (bonus bakiyesi silinir) birlikte
   * söyler — oyuncu neyi onayladığını bilmelidir.
   */
  const cancelClaim = async (claimItem) => {
    const remaining = claimRemainingWager(claimItem);
    const confirmed = window.confirm(
      `${claimItem.bonus_title || 'Bonusunuz'} iptal edilecek.\n\n`
      + `Kalan ${remaining.toFixed(2)} ₺ çevrim şartı kalkar ve para çekme `
      + 'yeniden açılır. Bonus bakiyeniz (bonusla kazandıklarınız dâhil) '
      + 'silinir; kendi bakiyenize dokunulmaz.\n\nDevam edilsin mi?',
    );
    if (!confirmed) return;
    setCancellingClaimId(claimItem.id);
    try {
      const { data } = await client.post(
        `/bonuses/claims/${claimItem.id}/cancel`);
      setWalletBalances(data);
      await refreshRewards();
      const revoked = Number(data?.revoked || 0);
      toast({
        title: 'Bonus iptal edildi',
        description: revoked > 0
          ? `${revoked.toFixed(2)} ₺ bonus bakiyeniz silindi, çevrim şartınız kalktı.`
          : 'Çevrim şartınız kalktı.',
      });
    } catch (error) {
      toast({
        title: 'Bonus iptal edilemedi',
        description: error.response?.data?.detail || 'Lütfen tekrar deneyin.',
        variant: 'destructive',
      });
    } finally {
      setCancellingClaimId(null);
    }
  };

  /** Freespin hakkından vazgeçme — kaynağına göre farklı uca gider. */
  const cancelFreespin = async (item) => {
    const confirmed = window.confirm(
      `${Number(item.spin_count || 0)} freespin hakkınız iptal edilecek ve `
      + 'kullanılamaz hâle gelecek.\n\nDevam edilsin mi?',
    );
    if (!confirmed) return;
    setCancellingFreespinId(item.id);
    try {
      if (item.source === 'bonus') {
        await client.post(`/bonuses/claims/${item.claim_id}/freespin/cancel`);
      } else {
        await client.post(`/anchor/throws/${item.id}/cancel`);
      }
      await refreshRewards();
      toast({
        title: 'Freespin hakkı iptal edildi',
        description: 'Hak kapatıldı; listeden geçmişe taşındı.',
      });
    } catch (error) {
      toast({
        title: 'Freespin iptal edilemedi',
        description: error.response?.data?.detail || 'Lütfen tekrar deneyin.',
        variant: 'destructive',
      });
    } finally {
      setCancellingFreespinId(null);
    }
  };

  if (!userId) return null;
  // Üç liste de boşken kartların hepsi kendini gizler; ödüllerin kendi
  // sayfasında (`/hesap/bonuslarim`) bu, oyuncuya boş bir ekran demekti.
  // Boş durum yalnızca yanıtlar geldikten sonra çizilir — önce çizilseydi
  // ödülü olan oyuncu bir an "hakkınız yok" görürdü.
  if (
    emptyState && loaded
    && !myClaims.length && !myFreespins.length && !myFreebets.length
  ) {
    return emptyState;
  }
  return (
    <>
      <BonusClaims
        items={myClaims}
        onCancel={cancelClaim}
        cancellingId={cancellingClaimId}
      />
      <FreespinRights
        items={myFreespins}
        onPlay={playRewardGame}
        onCancel={cancelFreespin}
        cancellingId={cancellingFreespinId}
      />
      <FreebetRights items={myFreebets} />
      {activeFreespinGame && (
        <GameModal
          game={activeFreespinGame}
          mode="real"
          onClose={() => setActiveFreespinGame(null)}
        />
      )}
    </>
  );
};

// Betrun Bonuslar sayfasının üst sekmeleri. Kampanyanın geçerli alanı
// (`eligible`) çoklu değer taşır; "Tüm oyunlar" kampanyası her sekmede durur.
export const BONUS_FILTERS = [
  { id: 'all', label: 'Hepsi' },
  { id: 'casino', label: 'Casino', match: ['slots', 'casino'] },
  { id: 'sports', label: 'Spor', match: ['sports'] },
];

export const bonusMatchesFilter = (bonus, filterId) => {
  const filter = BONUS_FILTERS.find((item) => item.id === filterId);
  if (!filter?.match) return true;
  const ids = parseBonusEligible(bonus?.eligible);
  if (!ids.length) return true;
  return ids.some((id) => filter.match.includes(id));
};

// Kartı görsel + "Bonus Al / Detay" düğmelerinden oluşan şemalar: bonusun
// bilgisi tasarımın içindedir, koşullar karta dokununca açılan detaydadır.
export const IMAGE_ONLY_BONUS_THEMES = new Set(['betrun']);

// Detay penceresinde koşul tablosu (min. yatırım, çevrim, talep zamanı,
// günlük hak …) çizilmeyen şemalar: koşulları kampanya metni anlatır,
// tablo yalnızca panelde görünür (Betrun isteği, 13.10.2026).
export const HIDDEN_BONUS_TERMS_THEMES = new Set(['betrun']);

// Kayıp bonusunun Bonuslar sayfasından talep edildiği şemalar: kampanya
// ızgarasının ilk kartı olarak çizilir (Detay penceresi hesap ekranının
// içeriğini taşır) ve profil menüsündeki kısayol düşer (lib/accountMenu.js
// `THEME_MENU`). Sayfa `/hesap/kayip-bonusu` adresiyle açılmaya devam eder;
// kural ve talep ucu (`POST /cashback/claim`) aynıdır.
export const LOSS_BONUS_ON_PROMOTIONS_THEMES = new Set(['merit', 'betrun']);

const Promotions = () => {
  const [items, setItems] = useState([]);
  const themeId = useActiveTheme();
  const imageOnlyCards = IMAGE_ONLY_BONUS_THEMES.has(themeId);
  const showBonusTerms = !HIDDEN_BONUS_TERMS_THEMES.has(themeId);
  const lossBonusHere = LOSS_BONUS_ON_PROMOTIONS_THEMES.has(themeId);
  const [lossDetail, setLossDetail] = useState('');
  const [bonusFilter, setBonusFilter] = useState('all');
  // HTML açıklama listeye girmez (orada düz özeti durur); pencere açılınca
  // kampanya başına bir kez istenir ve yeniden açılışta bellekten çizilir.
  const [detailHtml, setDetailHtml] = useState({ id: null, html: '', failed: false });
  const [loadError, setLoadError] = useState(false);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { user, setWalletBalances } = useAuth();
  const settings = useSettings();
  // Bonus kartındaki koşul şeridi (min. yatırım / max. bonus / çevrim).
  // Ayar gelmeden önce çizilir: alan hiç tanımlanmamış eski kurulumlarda da
  // eski görünüm sürsün diye kapatma yalnızca açık bir `false` ile olur.
  const showBonusStats = settings.bonus_card_stats_enabled !== false;
  const [busyId, setBusyId] = useState(null);
  const [selectedBonus, setSelectedBonus] = useState(null);
  const navigate = useNavigate();
  const selectedDetailId = selectedBonus?.description_html ? selectedBonus.id : null;
  useEffect(() => {
    if (!selectedDetailId) return undefined;
    if (detailHtml.id === selectedDetailId && !detailHtml.failed) return undefined;
    let cancelled = false;
    setDetailHtml({ id: null, html: '', failed: false });
    fetchBonusDescriptionHtml(selectedDetailId)
      .then((html) => {
        if (!cancelled) setDetailHtml({ id: selectedDetailId, html, failed: false });
      })
      .catch(() => {
        if (!cancelled) setDetailHtml({ id: selectedDetailId, html: '', failed: true });
      });
    return () => { cancelled = true; };
  }, [selectedDetailId]); // eslint-disable-line react-hooks/exhaustive-deps
  const userId = user?.id;
  // Talep ucu oturum ister; ziyaretçiye ve kartı çizmeyen şemaya istek atılmaz.
  const loss = useLossBonus({
    active: lossBonusHere && !!userId, quiet: true, withHistory: false,
  });
  // Promosyon kodunun Telegram kapısı bu sayfada değil kod ekranındadır
  // (`pages/PromoCode.jsx`); burada kalan kapı kampanyalarınkidir. Her kampanya
  // kendi kanalını yazabilir, bu yüzden üyelik kampanya başına sorulur
  // (`/telegram/link?bonus_id=`): kapı oyuncunun son dokunduğu kampanyanın
  // (yoksa listedeki ilkinin) kanalını gösterir. Bonusu siteden vermeyen
  // kampanyalarda (canlı destek, sayfa yönlendirmesi, otomatik tanımlama)
  // kapı anlamsızdır: talep bu karttan geçmez.
  const needsTelegram = (item) => (
    !!item?.require_telegram
    && !item.claim_via_support
    && !item.auto_award
    && !(item.claim_redirect_url || '').trim()
  );
  const [telegramTargetId, setTelegramTargetId] = useState('');
  const telegramBonuses = items.filter(needsTelegram);
  const telegramTarget = telegramBonuses.find((item) => item.id === telegramTargetId)
    || telegramBonuses[0] || null;
  const telegram = usePromoTelegram(!!telegramTarget, telegramTarget?.id || '');
  const promoTelegram = telegram.status;

  // Kayıp bonusu detayı kampanya detayıyla aynı pencere kalıbıdır.
  useEffect(() => {
    if (!lossDetail) return undefined;
    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setLossDetail('');
    };
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [lossDetail]);

  useEffect(() => {
    if (!selectedBonus) return undefined;

    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setSelectedBonus(null);
    };

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', closeOnEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [selectedBonus]);

  useEffect(() => {
    // 'no-cache' başlığı, panelden yeni eklenen kampanyanın tarayıcıda kalmış
    // eski kopya yüzünden gizlenmesini engeller (sunucu da no-store gönderir).
    // Oyuncunun kendi ödülleri (bonus çevrimi, freespin, freebet) ayrı bir
    // bileşendedir (`MyRewards`) ve kendi isteklerini atar.
    setLoading(true);
    client.get('/bonuses', { headers: { 'Cache-Control': 'no-cache' } })
      .then((bonusesResponse) => {
        setItems(bonusesResponse.data.items || []);
        setLoadError(false);
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, [userId]);

  const claim = async (b) => {
    if (!user) { toast({ title: 'Giriş gerekli', description: 'Bonusu almak için üye girişi yapın' }); return; }
    setBusyId(b.id);
    try {
      const { data } = await client.post(`/bonuses/${b.id}/claim`);
      setWalletBalances(data);
      // Kampanya listesi yeniden okunur ki düğmenin kilidi
      // (`claim_locked`) sunucudan güncel gelsin. Oyuncunun ödül kartları
      // hesap bölümündedir (`/hesap/bonuslarim`) ve kendi isteğini atar.
      try {
        const { data: bonusesData } = await client.get(
          '/bonuses', { headers: { 'Cache-Control': 'no-cache' } });
        setItems(bonusesData.items || []);
      } catch {
        // Bonus başarıyla alındıysa yalnızca liste yenilemesinin hatası
        // işlemin kendisini başarısızmış gibi göstermemeli.
      }
      toast({ title: 'Bonus Alındı', description: claimSummary(data) });
    } catch (e) {
      if (b.require_telegram && [403, 503].includes(e.response?.status)) {
        if (telegramTarget?.id === b.id) telegram.refresh();
        else setTelegramTargetId(b.id);
      }
      toast({ title: 'Hata', description: e.response?.data?.detail || 'Bonus alınamadı' });
    } finally { setBusyId(null); }
  };

  // Kampanya butonunun kilidi sunucudan gelir: günlük hakkı olan bir bonus
  // bir kez alınmış olsa da yeniden alınabilir, "Bonus Alındı" yazmamalı.
  // Kilit sunucudan gelir (`claim_locked`): günlük hakkı olan bir bonus bir
  // kez alınmış olsa da yeniden alınabilir.
  const bonusLocked = (bonus) => !!bonus.claim_locked;

  // Panelde "canlı destekten talep edilir" işaretlenen kampanya siteden
  // otomatik alınmaz: uç da bu kayda kapalıdır, buton yalnızca oyuncuyu
  // temsilciye bağlar. Kilit/ günlük hak sayaçları bu kampanyada anlamsızdır.
  const chatState = liveChatState(settings, user);
  const isSupportBonus = (bonus) => !!bonus?.claim_via_support;
  const requestFromSupport = () => {
    if (chatState === 'login_required') {
      toast({
        title: 'Giriş gerekli',
        description: 'Canlı desteğe bağlanmak için üye girişi yapın',
      });
      return;
    }
    if (!openLiveChat()) {
      toast({
        title: 'Canlı destek açılamadı',
        description: 'Sayfayı yenileyip tekrar deneyin.',
      });
    }
  };

  const bonusAction = (bonus) => bonusClaimAction(bonus, {
    locked: bonusLocked(bonus),
    chatState,
  });
  // Durumu bilinen tek kanal kapının gösterdiği kampanyanınkidir; başka bir
  // kampanyanın kanalı ona dokunulunca sorulur.
  const telegramBlocked = (bonus) => (
    needsTelegram(bonus)
    && (bonus.id !== telegramTarget?.id || !promoTelegram?.member)
  );
  const bonusActionDisabled = (bonus) => (
    bonusAction(bonus).disabled || busyId === bonus.id
  );
  const bonusActionLabel = (bonus) => {
    const action = bonusAction(bonus);
    return telegramBlocked(bonus) && !action.disabled
      ? 'Telegram ile Doğrula'
      : action.label;
  };

  const handleBonusAction = (bonus) => {
    const action = bonusAction(bonus);
    // Ödülü kendi sayfasından alınan kampanya (çark, çapa): kart bonusu talep
    // etmez, oyuncuyu oraya götürür. Adres backend'de site içiyle sınırlıdır.
    // Otomatik kampanyada da talep yoktur: düğme yatırım sayfasını açar,
    // hak yatırım onaylanınca arka planda tanımlanır.
    if (action.mode === 'redirect' || action.mode === 'auto') {
      setSelectedBonus(null);
      navigate(action.href);
      return;
    }
    if (isSupportBonus(bonus)) {
      requestFromSupport();
      return;
    }
    if (bonus.claim_state === 'first_deposit_required') {
      navigate('/hesap/para-yatir');
      return;
    }
    if (telegramBlocked(bonus)) {
      setTelegramTargetId(bonus.id);
      setSelectedBonus(null);
      window.requestAnimationFrame(() => {
        document.getElementById('promo-telegram-title')?.scrollIntoView({
          behavior: 'smooth', block: 'center',
        });
      });
      toast({
        title: 'Telegram doğrulaması gerekli',
        description: user
          ? 'Bu bonusu almak için Telegram hesabınızı bağlayıp kanala katılın.'
          : 'Bu bonusu almak için önce üye girişi yapın.',
      });
      return;
    }
    claim(bonus);
  };

  // Promosyon kodu köprüsü. Betrun'da ziyaretçiye çizilmez (kodu ancak üye
  // kullanabilir, soru kapıdan dönen bir davet gibi duruyordu) ve başlık
  // şemanın düz yazı tipiyle yazılır — altın gradyanlı başlık sayfanın geri
  // kalanından kopuk duruyordu.
  const promoCodeBridge = (
    <Link
      to="/promosyon-kodu"
      className="promo-code-bridge pusu-card mb-6 flex items-center gap-3 p-4 transition-colors hover:border-[var(--pusu-gold)]/40"
    >
      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[var(--pusu-gold)]/10 text-[var(--pusu-gold)]">
        <TicketPercent size={20} />
      </div>
      <div className="min-w-0 flex-1">
        <div className={imageOnlyCards ? 'font-bold text-white' : 'font-black gold-text'}>
          Promosyon Kodunuz mu var?
        </div>
        <p className="mt-0.5 text-xs text-gray-500">
          Kodunuzu kullanın, geçmiş kullanımlarınızı görün.
        </p>
      </div>
      <ArrowRight size={18} className="shrink-0 text-[var(--pusu-gold)]" />
    </Link>
  );
  const visibleItems = imageOnlyCards
    ? items.filter((bonus) => bonusMatchesFilter(bonus, bonusFilter))
    : items;
  const lossBlocks = imageOnlyCards
    ? loss.blocks.filter((block) => lossBlockMatchesFilter(block, bonusFilter))
    : loss.blocks;
  const lossDetailBlock = loss.blocks.find((block) => block.vertical === lossDetail) || null;
  const lossTileProps = (block) => ({
    data: loss.data,
    block,
    busy: loss.busy === block.vertical,
    onClaim: loss.claim,
    onDetail: setLossDetail,
  });

  return (
    <div className="pusu-body-gradient">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: 'rgba(var(--pusu-gold-rgb),0.12)' }}>
            <Gift size={22} className="text-[var(--pusu-gold)]" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-black gold-text">Bonuslar</h1>
            <p className="text-sm text-gray-400">Size özel bonuslar ve kampanyalar</p>
          </div>
        </div>

        {imageOnlyCards && user && promoCodeBridge}
        {imageOnlyCards && (
          <div className="bonus-filter-tabs mb-5 flex gap-2" role="tablist" aria-label="Bonus türü">
            {BONUS_FILTERS.map((filter) => (
              <button
                key={filter.id}
                type="button"
                role="tab"
                aria-selected={bonusFilter === filter.id}
                onClick={() => setBonusFilter(filter.id)}
                className={`provider-chip ${bonusFilter === filter.id ? 'active' : ''}`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        )}

        {/* Çekiliş kendi sayfasındadır ve menüde yalnızca yayında bir
            çekiliş varken görünür; kampanyaları buradan takip eden oyuncu
            onu kaçırmamalıdır. Köprü de aynı bayrağa bağlıdır. */}
        {settings.raffle_active === true && (
          <Link
            to="/cekilis"
            className="pusu-card mb-6 flex items-center gap-3 p-4 transition-colors hover:border-[var(--pusu-gold)]/40"
          >
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[var(--pusu-gold)]/10 text-[var(--pusu-gold)]">
              <Ticket size={20} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-black gold-text">Çekiliş biletiniz hazır mı?</div>
              <p className="mt-0.5 text-xs text-gray-500">
                Yatırımlarınızdan kazandığınız biletleri ve ödülleri görün.
              </p>
            </div>
            <ArrowRight size={18} className="shrink-0 text-[var(--pusu-gold)]" />
          </Link>
        )}

        {/* Tahmin etkinliği de kendi sayfasındadır; köprü aynı bayrağa bağlıdır
            (yayında ya da yakın zamanda sonuçlanmış etkinlik varken). */}
        {settings.prediction_active === true && (
          <Link
            to="/etkinlik"
            className="pusu-card mb-6 flex items-center gap-3 p-4 transition-colors hover:border-[var(--pusu-gold)]/40"
          >
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[var(--pusu-gold)]/10 text-[var(--pusu-gold)]">
              <Target size={20} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-black gold-text">Skor tahmin etkinliği</div>
              <p className="mt-0.5 text-xs text-gray-500">
                Maç başlamadan tahminini gönder, doğru bilenler ödül kazansın.
              </p>
            </div>
            <ArrowRight size={18} className="shrink-0 text-[var(--pusu-gold)]" />
          </Link>
        )}

        {/* Kod girişi kendi ekranındadır; kabukların üst menüsünde "Promo Kod"
            düğmesi olmayan şemalarda (klasik, Herkül) oyuncunun oraya
            ulaşabileceği tek yol bu köprüdür. Betrun'da köprü sayfanın en
            üstündedir (aşağıda) ve yalnızca üyeye çizilir. */}
        {!imageOnlyCards && promoCodeBridge}

        {telegramTarget && !promoTelegram?.member && (
          <PromoTelegramGate
            key={telegramTarget.id}
            title={`${telegramTarget.title}: Telegram kanalına katılın`}
            user={user}
            loading={telegram.loading}
            status={promoTelegram}
            linking={telegram.linking}
            linkUrl={telegram.linkUrl}
            onLink={telegram.link}
            onRefresh={telegram.refresh}
            purpose={telegramTarget.is_trial_bonus ? 'trial' : 'bonus'}
          />
        )}

        {loading ? (
          <div className="text-center py-16 text-gray-400"><Loader2 className="inline animate-spin mr-2" /> Yükleniyor...</div>
        ) : loadError ? (
          <div className="text-center py-16 text-gray-500">
            Kampanyalar yüklenemedi. Lütfen sayfayı yenileyin.
          </div>
        ) : visibleItems.length === 0 && lossBlocks.length === 0 ? (
          <div className="text-center py-16 text-gray-500">Aktif kampanya bulunmuyor</div>
        ) : imageOnlyCards ? (
          <div className="bonus-image-grid" data-testid="bonus-image-grid">
            {lossBlocks.map((block) => (
              <LossBonusImageTile key={`loss-${block.vertical}`} {...lossTileProps(block)} />
            ))}
            {visibleItems.map((p) => (
              // Kart görsel + iki düğmedir: ödül ve koşullar tasarımın
              // içindedir, altında yalnızca "Bonus Al" ve "Detay" durur.
              <div
                key={p.id}
                className="bonus-image-item"
                {...hook(HOOKS.BONUS_CARD)}
                {...bonusIdHook(p.id)}
              >
                <button
                  type="button"
                  className="bonus-image-card"
                  onClick={() => setSelectedBonus(p)}
                  aria-label={`${p.title} detaylarını göster`}
                  {...bonusHook('media')}
                >
                  {p.image ? (
                    <img
                      src={resolveApiAssetUrl(p.image)}
                      alt={p.title}
                      loading="lazy"
                      {...bonusHook('image')}
                    />
                  ) : bonusHasHtmlArt(p) ? (
                    <span className="bonus-image-card-html" {...bonusHook('image')}>
                      <BonusHtmlThumb bonusId={p.id} title={p.title} />
                    </span>
                  ) : (
                    // Görseli yüklenmemiş kampanya boş bir kutu olmasın; başlık
                    // görselin yerini tutar (panelde görsel yüklenince kalkar).
                    <span className="bonus-image-card-fallback" {...bonusHook('title')}>{p.title}</span>
                  )}
                </button>
                <div className="bonus-image-actions" {...bonusHook('actions')}>
                  <button
                    type="button"
                    disabled={bonusActionDisabled(p)}
                    onClick={() => handleBonusAction(p)}
                    aria-haspopup={isSupportBonus(p) ? 'dialog' : undefined}
                    aria-label={isSupportBonus(p)
                      ? `${p.title} için canlı desteği aç`
                      : undefined}
                    title={isSupportBonus(p) && chatState === 'off'
                      ? 'Canlı destek şu anda kapalı'
                      : undefined}
                    className="gold-btn bonus-image-action"
                    {...bonusHook('claim')}
                  >
                    {isSupportBonus(p)
                      ? <Headphones size={14} className="shrink-0" />
                      : busyId === p.id && <Loader2 size={14} className="animate-spin" />}
                    {bonusActionLabel(p)}
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedBonus(p)}
                    className="gold-outline-btn bonus-image-action"
                    {...bonusHook('detail')}
                  >
                    Detay
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {lossBlocks.map((block) => (
              <LossBonusCard key={`loss-${block.vertical}`} {...lossTileProps(block)} />
            ))}
            {visibleItems.map((p) => (
              <div
                key={p.id}
                className="pusu-card overflow-hidden group"
                {...hook(HOOKS.BONUS_CARD)}
                {...bonusIdHook(p.id)}
              >
                <div className="relative aspect-[16/9] overflow-hidden" {...bonusHook('media')}>
                  {p.image
                    ? <img src={resolveApiAssetUrl(p.image)} alt={p.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" {...bonusHook('image')} />
                    : bonusHasHtmlArt(p) && <BonusHtmlThumb bonusId={p.id} title={p.title} />}
                  {/* Karartma yalnızca alt kenarda: banner tasarımları zaten koyu,
                      tam kaplayan katman görseli okunmaz hâle getiriyordu. */}
                  {!bonusHasHtmlArt(p) && <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0) 50%, rgba(0,0,0,0.55) 100%)' }} />}
                  {p.badge && <span className="absolute top-3 left-3 gold-btn px-2.5 py-1 rounded text-[10px] font-black" {...bonusHook('badge')}>{p.badge}</span>}
                  {/* Yüzdeli kampanyada başlık boştur: oranı banner görselinin
                      kendi tasarımı söyler (bkz. lib/bonusReward.js). */}
                  {/* Şablon görsel alanındaysa başlık onun üstüne binerdi; ödül
                      zaten alttaki koşul şeridinde yazar. */}
                  {bonusRewardHeadline(p) && !bonusHasHtmlArt(p) && (
                    <div
                      className="absolute bottom-3 left-3 text-3xl font-black text-white"
                      style={{ textShadow: '0 2px 8px rgba(0,0,0,0.75)' }}
                      {...bonusHook('headline')}
                    >
                      {bonusRewardHeadline(p)}
                    </div>
                  )}
                </div>
                <div className="p-5" {...bonusHook('body')}>
                  <h3 className="font-bold text-lg text-white" {...bonusHook('title')}>{p.title}</h3>
                  <BonusCardDescription bonus={p} onOpen={() => setSelectedBonus(p)} />
                  {bonusHighlight(p) && (
                    <div className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-emerald-400" {...bonusHook('highlight')}>
                      <CheckCircle2 size={14} />
                      {bonusHighlight(p)}
                    </div>
                  )}
                  {Number(p.daily_claim_limit) > 0 && (
                    <div className="mt-3 text-xs font-semibold text-yellow-400" {...bonusHook('daily-limit')}>
                      24 saatte {Number(p.daily_claim_limit)} kez alınabilir
                      {p.daily_claim_remaining !== undefined
                        && ` • kalan hakkın: ${p.daily_claim_remaining}`}
                    </div>
                  )}
                  {/* Koşul şeridi panelden kapatılabilir (Ayarlar → Tasarım):
                      bazı kurulumlar min. yatırım/çevrim rakamlarını kartta
                      göstermek istemez, koşullar detay penceresinde kalır.
                      Deneme bonusunda "Yatırım: Gerekmez" sütunu çizilmez;
                      kart iki sütuna iner. */}
                  {showBonusStats && (
                  <div className={`grid ${p.is_trial_bonus ? 'grid-cols-2' : 'grid-cols-3'} gap-2 text-xs mt-3 pt-3 border-t border-white/5`} {...bonusHook('stats')}>
                    <div {...bonusHook('stat')}>
                      <div className="text-gray-500">
                        {bonusPrimaryMetric(p).label}
                      </div>
                      <div className="font-bold text-white">
                        {bonusPrimaryMetric(p).value}
                      </div>
                    </div>
                    {!p.is_trial_bonus && (
                      <div {...bonusHook('stat')}>
                        <div className="text-gray-500">
                          {isFreespinOnlyBonus(p) || isCashTierBonus(p) ? 'Min. Yat.' : 'Max. Bonus'}
                        </div>
                        <div className="font-bold text-green-400">
                          {isFreespinOnlyBonus(p) || isCashTierBonus(p)
                            ? bonusMinDeposit(p) : bonusMaxBonus(p)} ₺
                        </div>
                      </div>
                    )}
                    <div {...bonusHook('stat')}><div className="text-gray-500">Çevrim</div><div className="font-bold text-yellow-400">x{p.wager_multiplier}</div></div>
                  </div>
                  )}
                  <div className="flex gap-2 mt-4" {...bonusHook('actions')}>
                    <button
                      type="button"
                      {...bonusHook('claim')}
                      disabled={bonusActionDisabled(p)}
                      onClick={() => handleBonusAction(p)}
                      aria-haspopup={isSupportBonus(p) ? 'dialog' : undefined}
                      aria-label={isSupportBonus(p)
                        ? `${p.title} için canlı desteği aç`
                        : undefined}
                      title={isSupportBonus(p) && chatState === 'off'
                        ? 'Canlı destek şu anda kapalı'
                        : undefined}
                      className="gold-btn flex-1 py-2.5 rounded-lg text-sm leading-tight flex items-center justify-center gap-1 disabled:opacity-50"
                    >
                      {isSupportBonus(p)
                        ? <Headphones size={14} className="shrink-0" />
                        : busyId === p.id && <Loader2 size={14} className="animate-spin" />}
                      {bonusActionLabel(p)}
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedBonus(p)}
                      {...bonusHook('detail')}
                      className="gold-outline-btn px-4 py-2.5 rounded-lg text-sm"
                      aria-label={`${p.title} detaylarını göster`}
                    >
                      Detay
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {lossDetailBlock && loss.data && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="loss-bonus-detail-title"
          onClick={() => setLossDetail('')}
        >
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" aria-hidden="true" />
          <div
            className="pusu-card relative w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setLossDetail('')}
              className="gold-outline-btn absolute right-3 top-3 z-10 grid h-9 w-9 place-items-center rounded-full bg-black/60"
              aria-label="Bonus detayını kapat"
            >
              <X size={17} />
            </button>
            <div className="p-5 md:p-6">
              <LossBonusDetail
                compact
                data={loss.data}
                block={lossDetailBlock}
                busy={loss.busy === lossDetailBlock.vertical}
                onClaim={loss.claim}
              />
            </div>
          </div>
        </div>
      )}

      {selectedBonus && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="bonus-detail-title"
          onClick={() => setSelectedBonus(null)}
          {...bonusHook('overlay')}
        >
          <div
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            aria-hidden="true"
          />
          <div
            className={`pusu-card relative w-full ${selectedBonus.description_html ? 'max-w-2xl' : 'max-w-lg'} max-h-[90vh] overflow-y-auto`}
            onClick={(event) => event.stopPropagation()}
            {...hook(HOOKS.BONUS_MODAL)}
            {...bonusIdHook(selectedBonus.id)}
          >
            {selectedBonus.image && (
              <div className="relative aspect-[16/7] overflow-hidden" {...bonusHook('media')}>
                <img
                  {...bonusHook('image')}
                  src={resolveApiAssetUrl(selectedBonus.image)}
                  alt=""
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div
                  className="absolute inset-0"
                  style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0) 55%, rgba(8,5,14,0.85) 100%)' }}
                />
                {selectedBonus.badge && (
                  <span className="gold-btn absolute left-4 top-4 rounded px-2.5 py-1 text-[10px] font-black" {...bonusHook('badge')}>
                    {selectedBonus.badge}
                  </span>
                )}
                {bonusRewardHeadline(selectedBonus) && (
                  <div
                    className="absolute bottom-4 left-5 text-4xl font-black text-white"
                    style={{ textShadow: '0 2px 8px rgba(0,0,0,0.75)' }}
                    {...bonusHook('headline')}
                  >
                    {bonusRewardHeadline(selectedBonus)}
                  </div>
                )}
              </div>
            )}

            <button
              type="button"
              onClick={() => setSelectedBonus(null)}
              {...bonusHook('close')}
              className="gold-outline-btn absolute right-3 top-3 z-10 grid h-9 w-9 place-items-center rounded-full bg-black/60"
              aria-label="Bonus detayını kapat"
            >
              <X size={17} />
            </button>

            <div className="p-5 md:p-6" {...bonusHook('body')}>
              <h2 id="bonus-detail-title" className="pr-10 text-xl font-black text-white" {...bonusHook('title')}>
                {selectedBonus.title}
              </h2>
              {bonusHighlight(selectedBonus) && (
                <div className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-emerald-400" {...bonusHook('highlight')}>
                  <CheckCircle2 size={14} />
                  {bonusHighlight(selectedBonus)}
                </div>
              )}
              {/* Açıklama HTML ise yöneticinin şablonu yalıtılmış çerçevede
                  çizilir (bkz. lib/richHtmlFrame.js); listede duran düz özet
                  yalnızca kart içindir. */}
              {selectedBonus.description_html ? (
                <div className="mt-3" {...bonusHook('description')}>
                  {detailHtml.id === selectedBonus.id ? (
                    detailHtml.failed ? (
                      <p className="whitespace-pre-line text-sm leading-6 text-gray-300">
                        {selectedBonus.description}
                      </p>
                    ) : (
                      <RichHtmlFrame
                        html={detailHtml.html}
                        title={`${selectedBonus.title} detayı`}
                      />
                    )
                  ) : (
                    <div className="flex justify-center py-6 text-gray-500">
                      <Loader2 size={18} className="animate-spin" />
                    </div>
                  )}
                </div>
              ) : (
                <p className="mt-3 whitespace-pre-line text-sm leading-6 text-gray-300" {...bonusHook('description')}>
                  {selectedBonus.description || 'Bu bonus için ek açıklama bulunmuyor.'}
                </p>
              )}

              {showBonusTerms && (
              <div className="mt-5 grid grid-cols-2 gap-3 border-y border-white/5 py-4 text-sm" {...bonusHook('terms')}>
                {isFreespinOnlyBonus(selectedBonus) ? (
                  <>
                    <div>
                      <div className="text-xs text-gray-500">Freespin Ödülü</div>
                      <div className="mt-1 font-bold text-violet-300">
                        {freespinRewardText(selectedBonus)}
                      </div>
                    </div>
                    {!selectedBonus.is_trial_bonus && (
                      <div>
                        <div className="text-xs text-gray-500">Minimum Yatırım</div>
                        <div className="mt-1 font-bold text-green-400">
                          {bonusMinDeposit(selectedBonus)} ₺
                        </div>
                      </div>
                    )}
                  </>
                ) : selectedBonus.is_trial_bonus ? (
                  <div>
                    <div className="text-xs text-gray-500">Deneme Bonus Tutarı</div>
                    <div className="mt-1 font-bold text-white">{selectedBonus.trial_amount} ₺</div>
                  </div>
                ) : (
                  <>
                    {/* Bonus oranı oyuncuya yazılmaz; kampanyanın anlattığı
                        ödül banner ve açıklama metnindedir. */}
                    <div>
                      <div className="text-xs text-gray-500">Minimum Yatırım</div>
                      <div className="mt-1 font-bold text-green-400">{bonusMinDeposit(selectedBonus)} ₺</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Maksimum Bonus</div>
                      <div className="mt-1 font-bold text-green-400">{bonusMaxBonus(selectedBonus)} ₺</div>
                    </div>
                  </>
                )}
                <div>
                  <div className="text-xs text-gray-500">Çevrim Şartı</div>
                  <div className="mt-1 font-bold text-yellow-400">x{selectedBonus.wager_multiplier}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Geçerlilik Süresi</div>
                  <div className="mt-1 font-bold text-white">{selectedBonus.valid_days} gün</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Geçerli Alan</div>
                  <div className="mt-1 font-bold text-white">
                    {formatBonusEligible(selectedBonus.eligible)}
                  </div>
                </div>
                {/* Yatırım bonusu, yatırım oynanmadan alınır: koşulu ödemeden
                    önce söylemek "bonusu alamıyorum" şikâyetini önler. */}
                {isAutoBonus(selectedBonus) && (
                  <div>
                    <div className="text-xs text-gray-500">Talep Yolu</div>
                    <div className="mt-1 font-bold text-emerald-400">
                      Otomatik — yatırım onaylanınca
                    </div>
                  </div>
                )}
                {bonusWindowText(selectedBonus) && (
                  <div>
                    <div className="text-xs text-gray-500">Yatırım Saati</div>
                    <div className="mt-1 font-bold text-white">
                      {bonusWindowText(selectedBonus)}
                    </div>
                  </div>
                )}
                {isAutoBonus(selectedBonus)
                  && Number(selectedBonus.auto_daily_limit) > 0 && (
                  <div>
                    <div className="text-xs text-gray-500">Günlük Hak</div>
                    <div className="mt-1 font-bold text-yellow-400">
                      Günde {Number(selectedBonus.auto_daily_limit)} yatırım
                    </div>
                  </div>
                )}
                {!selectedBonus.is_trial_bonus
                  && !isAutoBonus(selectedBonus)
                  && !selectedBonus.require_lost_deposit
                  && selectedBonus.require_unspent_deposit !== false && (
                  <div>
                    <div className="text-xs text-gray-500">Talep Zamanı</div>
                    <div className="mt-1 font-bold text-white">
                      Yatırımı oynamadan önce
                    </div>
                  </div>
                )}
                {/* İade kampanyasının şartı da ödemeden önce yazılır: oyuncu
                    bonusu yatırımın hemen ardından almaya çalışıp reddedilince
                    "bonus vermiyorlar" diye canlı desteğe gidiyordu. */}
                {!selectedBonus.is_trial_bonus
                  && !!selectedBonus.require_lost_deposit && (
                  <div>
                    <div className="text-xs text-gray-500">Talep Zamanı</div>
                    <div className="mt-1 font-bold text-amber-400">
                      Yatırımı kaybettikten sonra
                    </div>
                  </div>
                )}
                {isSupportBonus(selectedBonus) && (
                  <div>
                    <div className="text-xs text-gray-500">Talep Yolu</div>
                    <div className="mt-1 font-bold text-emerald-400">
                      Canlı destek
                    </div>
                  </div>
                )}
                {!isAutoBonus(selectedBonus)
                  && Number(selectedBonus.daily_claim_limit) > 0 && (
                  <div>
                    <div className="text-xs text-gray-500">Günlük Hak</div>
                    <div className="mt-1 font-bold text-yellow-400">
                      24 saatte {Number(selectedBonus.daily_claim_limit)} kez
                      {selectedBonus.daily_claim_remaining !== undefined
                        && ` • kalan ${selectedBonus.daily_claim_remaining}`}
                    </div>
                  </div>
                )}
              </div>
              )}

              {/* Kademeli kampanyada oyuncu hangi yatırımın kaç spin (ya da
                  kaç ₺ bonus) getirdiğini ödemeden önce görmelidir. */}
              {tierLines(selectedBonus).length > 0 && (
                <div className="mt-4" data-testid="bonus-tier-table" {...bonusHook('tiers')}>
                  <div className="text-xs text-gray-500">Yatırım Kademeleri</div>
                  <div className="mt-2 divide-y divide-white/5 overflow-hidden rounded-lg border border-white/5 text-sm">
                    {tierLines(selectedBonus).map((row) => (
                      <div key={row.range} className="flex items-center justify-between gap-3 px-3 py-2">
                        <span className="text-gray-300">{row.range}</span>
                        <span className={`font-bold ${row.kind === REWARD_CASH ? 'text-green-400' : 'text-violet-300'}`}>
                          {row.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <button
                type="button"
                disabled={bonusActionDisabled(selectedBonus)}
                onClick={() => handleBonusAction(selectedBonus)}
                {...bonusHook('claim')}
                aria-haspopup={isSupportBonus(selectedBonus) ? 'dialog' : undefined}
                title={isSupportBonus(selectedBonus) && chatState === 'off'
                  ? 'Canlı destek şu anda kapalı'
                  : undefined}
                className="gold-btn mt-5 flex w-full items-center justify-center gap-2 rounded-lg py-3 text-sm disabled:opacity-50"
              >
                {isSupportBonus(selectedBonus)
                  ? <Headphones size={15} />
                  : busyId === selectedBonus.id && <Loader2 size={14} className="animate-spin" />}
                {bonusActionLabel(selectedBonus)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Promotions;
